import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '../services/api';
import { PayrollActivityEntry, PayrollRecord } from '../types';

// How often the feed checks for new entries while the tab is visible.
// Only Super Admins with the Payroll page open poll at all, and each
// poll asks only for entries newer than the last one seen.
const POLL_MS = 15_000;

function peso(value: unknown): string {
  const n = typeof value === 'number' ? value : Number(value);
  return `₱${(Number.isFinite(n) ? n : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function signatoryText(s: any): string {
  if (!s) return '—';
  return [s.name, s.title].filter(Boolean).join(', ') || '(blank)';
}

function describe(entry: PayrollActivityEntry): string {
  const d = entry.details ?? {};
  switch (entry.action) {
    case 'scholar_removed':
      return `removed ${d.name ?? 'a scholar'} from the payroll`;
    case 'scholar_added':
      return `added ${d.name ?? 'a scholar'} to the payroll`;
    case 'signatory_changed':
      return `changed the "${d.label ?? 'signatory'}" signatory from "${signatoryText(d.from)}" to "${signatoryText(d.to)}"`;
    case 'amount_changed':
      return `changed the amount from ${peso(d.from)} to ${peso(d.to)}`;
    case 'semester_changed':
      return `changed the semester from "${d.from || '(blank)'}" to "${d.to || '(blank)'}"`;
    case 'excel_downloaded':
      return `downloaded the payroll as Excel${d.periodLabel ? ` (${d.periodLabel})` : ''}: ${d.scholarCount ?? 0} scholar${d.scholarCount === 1 ? '' : 's'}, ${peso(d.total)} total — copy saved`;
    case 'printed':
      return `printed the payroll${d.periodLabel ? ` (${d.periodLabel})` : ''}: ${d.scholarCount ?? 0} scholar${d.scholarCount === 1 ? '' : 's'}, ${peso(d.total)} total — copy saved`;
    default:
      return entry.action;
  }
}

function formatTime(value: string): string {
  return new Date(value).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * The Super Admin's view of payroll work: every change made on the
 * Payroll page as it happens, and every printed or downloaded copy.
 */
const PayrollMonitor = () => {
  const [activity, setActivity] = useState<PayrollActivityEntry[]>([]);
  const [records, setRecords] = useState<PayrollRecord[]>([]);
  const [error, setError] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const lastIdRef = useRef<number | undefined>(undefined);

  const loadRecords = useCallback(() => {
    apiService
      .getPayrollRecords(1, 20)
      .then((result) => setRecords(result.data))
      .catch((err) => setError(err.message || 'Failed to load saved payrolls'));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const poll = () => {
      if (document.hidden) return;
      apiService
        .getPayrollActivity(lastIdRef.current)
        .then((fresh) => {
          if (cancelled || fresh.length === 0) return;
          lastIdRef.current = fresh[0].id;
          setActivity((prev) => [...fresh, ...prev].slice(0, 200));
          // A new print or download means a new saved copy to list.
          if (fresh.some((e) => e.action === 'printed' || e.action === 'excel_downloaded')) loadRecords();
        })
        .catch(() => {
          // Non-fatal — the next tick tries again.
        });
    };

    poll();
    loadRecords();
    const interval = setInterval(poll, POLL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [loadRecords]);

  const handleDownload = async (record: PayrollRecord) => {
    setDownloadingId(record.id);
    setError('');
    try {
      const blob = await apiService.downloadPayrollRecord(record.id);
      // A download rather than window.open: opening a window after an
      // await is treated as a pop-up and commonly blocked.
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = record.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err: any) {
      setError(err.message || 'Failed to download the payroll copy');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="no-print" style={{ marginTop: '2rem' }}>
      {error && (
        <div className="alert-error" style={{ padding: '0.85rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Live Payroll Activity</h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Updates every 15 seconds</span>
        </div>
        {activity.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>No payroll changes yet.</p>
        ) : (
          <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
            {activity.map((entry) => (
              <div
                key={entry.id}
                style={{ padding: '0.5rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.88rem', display: 'flex', gap: '0.75rem' }}
              >
                <span style={{ color: 'var(--text-secondary)', whiteSpace: 'nowrap', minWidth: '7.5rem' }}>{formatTime(entry.createdAt)}</span>
                <span>
                  <strong>{entry.userName}</strong> {describe(entry)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Saved Payroll Copies</h3>
        </div>
        {records.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>No payrolls have been printed or downloaded yet.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>By</th>
                  <th>Period</th>
                  <th>Scholars</th>
                  <th>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr key={record.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatTime(record.createdAt)}</td>
                    <td>{record.format === 'excel' ? 'Excel download' : 'Printed (PDF)'}</td>
                    <td>{record.printedBy}</td>
                    <td>{record.periodLabel || '—'}</td>
                    <td>{record.scholarCount}</td>
                    <td>{peso(record.total)}</td>
                    <td>
                      <button
                        className="btn btn-outline btn-sm"
                        type="button"
                        disabled={downloadingId === record.id}
                        onClick={() => handleDownload(record)}
                      >
                        {downloadingId === record.id ? 'Downloading...' : record.format === 'excel' ? 'Download Excel' : 'Download PDF'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PayrollMonitor;
